/**
 * 週間版の日別状態（少ない / 通常 / 多い）
 *
 * 集計方法:
 * - 営業時間帯 9〜19 時の時間別混雑度を日別に集約
 * - 既定は平均（dailyAvgCongestion）。VITE_TV_DAILY_AGG=p75 で P75 に切替可能
 * - 閾値は rawToCongestionTier の low/mid 境界に合わせ、3段階へ統合
 *   ≤7 → 少ない / ≤12 → 通常 / ≥13 → 多い
 */

import { dailyAvgCongestion, TV_DISPLAY_HOUR_RANGE } from './tvCongestionDisplay';

export const WEEKLY_STATUS_LABELS = {
  low: '少ない',
  mid: '通常',
  high: '多い',
  none: 'データなし',
};

/** 'mean'（既定）| 'p75' — 環境変数 VITE_TV_DAILY_AGG で切替 */
export function getTvDailyAggregationMode() {
  const raw = String(import.meta.env.VITE_TV_DAILY_AGG || 'mean').toLowerCase();
  return raw === 'p75' ? 'p75' : 'mean';
}

function percentile(sortedAsc, p) {
  if (!sortedAsc.length) return null;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = (sortedAsc.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  const w = idx - lo;
  return sortedAsc[lo] * (1 - w) + sortedAsc[hi] * w;
}

/**
 * 時間別マップから日別集約値を返す（mean または p75）
 * @param {Record<number, number>|null|undefined} hourlyMap
 */
export function aggregateDailyCongestion(hourlyMap, mode = getTvDailyAggregationMode()) {
  if (!hourlyMap || typeof hourlyMap !== 'object') return null;
  const positives = TV_DISPLAY_HOUR_RANGE
    .map((h) => Number(hourlyMap[h]) || 0)
    .filter((v) => v > 0);
  if (!positives.length) return null;

  if (mode === 'p75') {
    const sorted = [...positives].sort((a, b) => a - b);
    const v = percentile(sorted, 0.75);
    return v == null ? null : Math.round(v);
  }

  return dailyAvgCongestion(hourlyMap);
}

/**
 * @param {number|null|undefined} raw
 * @returns {{ tier: string, statusLabel: string, value: number|null }}
 */
export const rawToWeeklyStatus = (raw) => {
  if (raw == null || Number.isNaN(Number(raw)) || Number(raw) <= 0) {
    return { tier: 'none', statusLabel: WEEKLY_STATUS_LABELS.none, value: null };
  }
  const v = Math.round(Number(raw));
  if (v <= 7) return { tier: 'low', statusLabel: WEEKLY_STATUS_LABELS.low, value: v };
  if (v <= 12) return { tier: 'mid', statusLabel: WEEKLY_STATUS_LABELS.mid, value: v };
  return { tier: 'high', statusLabel: WEEKLY_STATUS_LABELS.high, value: v };
};

/**
 * 時間別マップ → 日別状態。データなし日は value:null（0 として扱わない）
 * @param {Record<number, number>|null|undefined} hourlyMap
 */
export const hourlyMapToWeeklyDayStatus = (hourlyMap) => {
  if (!hourlyMap || typeof hourlyMap !== 'object') {
    return rawToWeeklyStatus(null);
  }
  const agg = aggregateDailyCongestion(hourlyMap);
  if (!agg || agg <= 0) {
    return rawToWeeklyStatus(null);
  }
  return rawToWeeklyStatus(agg);
};
